import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api';
import { writePrintDoc } from '../../utils/printUtils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ArrowLeft, FileText, Search, Printer, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import { exportToExcel } from '../../utils/excelExport';

const LoanApplicationReport = () => {
  const [loans, setLoans] = useState([]);
  const [branches, setBranches] = useState([]);
  const [entrepreneurs, setEntrepreneurs] = useState([]);
  const [plans, setPlans] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [branchFilter, setBranchFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [lRes, bRes, eRes, pRes] = await Promise.all([
          api.get('/loans', { params: { limit: 5000 } }),
          api.get('/branches'),
          api.get('/entrepreneurs', { params: { limit: 5000 } }),
          api.get('/business-plans').catch(() => ({ data: [] })),
        ]);
        setLoans(lRes.data || []);
        setBranches(bRes.data || []);
        setEntrepreneurs(eRes.data || []);
        setPlans(pRes.data || []);
      } catch { toast.error('Failed to load loan applications'); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const brName = (id) => branches.find(b => b.id === id)?.name || '-';
  const entById = (id) => entrepreneurs.find(e => e.id === id);
  const planFor = (entId) => plans.find(p => p.entrepreneur_id === entId);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return loans.filter(l => {
      if (branchFilter !== 'all' && l.branch_id !== branchFilter) return false;
      if (statusFilter !== 'all' && l.status !== statusFilter) return false;
      if (typeFilter !== 'all' && l.loan_type !== typeFilter) return false;
      if (!q) return true;
      const e = entById(l.entrepreneur_id);
      return (e?.name || '').toLowerCase().includes(q) || (e?.enrollment_no || '').toLowerCase().includes(q) || (l.loan_id || '').toLowerCase().includes(q);
    });
  }, [loans, branchFilter, statusFilter, typeFilter, search, entrepreneurs]);

  const handlePrint = () => {
    const rows = filtered.map((l, i) => {
      const e = entById(l.entrepreneur_id);
      const p = planFor(l.entrepreneur_id);
      return `<tr style="background:${i%2?'#f9fafb':'#fff'}"><td>${i+1}</td><td>${l.loan_id||''}</td><td>${e?.name||''}</td><td>${brName(l.branch_id)}</td><td>${l.loan_type||''}</td><td style="text-align:right">Rs.${Number(l.loan_amount||0).toLocaleString('en-IN')}</td><td style="text-align:right">Rs.${Number(l.outstanding_amount||0).toLocaleString('en-IN')}</td><td>${l.disbursement_date?new Date(l.disbursement_date).toLocaleDateString('en-IN'):''}</td><td>${l.status||''}</td><td>${p?p.status:'—'}</td></tr>`;
    }).join('');
    const html = `<!doctype html><html><head><title>Loan Applications — WWAGS MIS</title>
    <style>body{font-family:Arial,sans-serif;padding:24px;color:#0f172a}h1{color:#1e40af;font-size:20px;margin:0 0 4px}.meta{color:#64748b;font-size:12px;margin-bottom:14px}table{width:100%;border-collapse:collapse;font-size:11px}th{background:#1e40af;color:#fff;padding:8px;text-align:left}td{padding:6px 8px;border-bottom:1px solid #e2e8f0}@media print{body{-webkit-print-color-adjust:exact}@page{size:A4 landscape;margin:12mm}}</style>
    </head><body><h1>WWAGS MIS — Loan Applications (1B)</h1>
    <p class="meta">Branch: <b>${branchFilter==='all'?'All':brName(branchFilter)}</b> · Status: <b>${statusFilter}</b> · Type: <b>${typeFilter}</b> · Total: <b>${filtered.length}</b> · Generated: ${new Date().toLocaleString('en-IN')}</p>
    <table><thead><tr><th>#</th><th>Loan ID</th><th>Entrepreneur</th><th>Branch</th><th>Type</th><th>Amount</th><th>O/S</th><th>Disbursed</th><th>Loan Status</th><th>Plan Status</th></tr></thead><tbody>${rows||'<tr><td colspan="10" style="text-align:center;color:#94a3b8;padding:20px">No loan applications</td></tr>'}</tbody></table>
    </body></html>`;
    const w = window.open('', '_blank'); if (w) { writePrintDoc(w.document, html); setTimeout(() => w.print(), 500); }
  };

  const handleExcel = () => {
    if (!filtered.length) { toast.error('Nothing to export'); return; }
    const rows = filtered.map((l, i) => {
      const e = entById(l.entrepreneur_id);
      const p = planFor(l.entrepreneur_id);
      return {
        'S.No.': i + 1,
        'Loan ID': l.loan_id || '',
        'Entrepreneur': e?.name || '',
        'Enrollment': e?.enrollment_no || '',
        'Branch': brName(l.branch_id),
        'Loan Type': l.loan_type || '',
        'Loan Amount (Rs.)': Number(l.loan_amount || 0),
        'Outstanding (Rs.)': Number(l.outstanding_amount || 0),
        'Interest Rate (%)': l.interest_rate || 0,
        'Tenure (months)': l.tenure_months || 0,
        'EMI': Number(l.emi_amount || 0),
        'Disbursement Date': l.disbursement_date ? new Date(l.disbursement_date).toLocaleDateString('en-IN') : '',
        'Loan Status': l.status || '',
        'Business Plan Status': p ? p.status : '—',
      };
    });
    exportToExcel({ filename: 'WWAGS_Loan_Applications', sheetName: 'Loan Applications', rows });
  };

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/reports" className="text-slate-500 hover:text-slate-700"><ArrowLeft className="w-5 h-5" /></Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold flex items-center gap-2"><FileText className="w-5 h-5 text-blue-700" />1B. Loan Application Report</h1>
          <p className="text-sm text-slate-600">All loan applications with their corresponding business-plan status (where present).</p>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 grid grid-cols-1 md:grid-cols-5 gap-3">
        <div className="md:col-span-2">
          <Label className="text-xs">Search</Label>
          <div className="relative"><Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} className="pl-8" placeholder="Loan ID / entrepreneur / enrollment" /></div>
        </div>
        <div>
          <Label className="text-xs">Branch</Label>
          <Select value={branchFilter} onValueChange={setBranchFilter}><SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All</SelectItem>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.code} - {b.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Loan Type</Label>
          <Select value={typeFilter} onValueChange={setTypeFilter}><SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="CEF">CEF</SelectItem>
              <SelectItem value="CIF">CIF</SelectItem>
              <SelectItem value="Bank Loan">Bank Loan</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Loan Status</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}><SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 flex flex-wrap items-center gap-3 justify-between">
        <div className="text-sm"><span className="text-slate-500">Showing</span> <b>{filtered.length}</b> <span className="text-slate-500">of {loans.length} loans</span></div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handlePrint}><Printer className="w-4 h-4 mr-1" />Print</Button>
          <Button onClick={handleExcel} className="bg-emerald-700 hover:bg-emerald-800 text-white"><FileSpreadsheet className="w-4 h-4 mr-1" />Excel</Button>
        </div>
      </div>

      {loading ? <div className="text-center py-12 text-slate-400">Loading…</div> : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-blue-700 text-white sticky top-0"><tr>
                <th className="px-2 py-2 text-left">#</th>
                <th className="px-2 py-2 text-left">Loan ID</th>
                <th className="px-2 py-2 text-left">Entrepreneur</th>
                <th className="px-2 py-2 text-left">Branch</th>
                <th className="px-2 py-2 text-left">Type</th>
                <th className="px-2 py-2 text-right">Amount</th>
                <th className="px-2 py-2 text-right">O/S</th>
                <th className="px-2 py-2 text-left">Disbursed</th>
                <th className="px-2 py-2 text-center">Loan Status</th>
                <th className="px-2 py-2 text-center">Plan Status</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.length === 0 ? <tr><td colSpan={10} className="px-3 py-10 text-center text-slate-400">No loan applications match.</td></tr> : filtered.map((l, i) => {
                  const e = entById(l.entrepreneur_id);
                  const p = planFor(l.entrepreneur_id);
                  return (
                    <tr key={l.id} className={i % 2 ? 'bg-slate-50' : ''}>
                      <td className="px-2 py-1.5">{i + 1}</td>
                      <td className="px-2 py-1.5 font-mono text-[10px]">{l.loan_id}</td>
                      <td className="px-2 py-1.5 font-medium">{e?.name || '-'}</td>
                      <td className="px-2 py-1.5">{brName(l.branch_id)}</td>
                      <td className="px-2 py-1.5"><span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-800">{l.loan_type}</span></td>
                      <td className="px-2 py-1.5 text-right">Rs.{Number(l.loan_amount || 0).toLocaleString('en-IN')}</td>
                      <td className="px-2 py-1.5 text-right">Rs.{Number(l.outstanding_amount || 0).toLocaleString('en-IN')}</td>
                      <td className="px-2 py-1.5 text-[10px]">{l.disbursement_date ? new Date(l.disbursement_date).toLocaleDateString('en-IN') : '-'}</td>
                      <td className="px-2 py-1.5 text-center capitalize">{l.status}</td>
                      <td className="px-2 py-1.5 text-center text-[10px] capitalize">{p ? p.status.replace('_', ' ') : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoanApplicationReport;
